import React, { createContext, useContext, useReducer, useCallback } from 'react';

const NotificationContext = createContext();

// Notification Types
export const NOTIFICATION_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Action Types
const NOTIFICATION_ACTIONS = {
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
  CLEAR_ALL: 'CLEAR_ALL',
  UPDATE_NOTIFICATION: 'UPDATE_NOTIFICATION'
};

// Notification Reducer
const notificationReducer = (state, action) => {
  switch (action.type) {
    case NOTIFICATION_ACTIONS.ADD_NOTIFICATION:
      return [...state, action.payload];
    
    case NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION:
      return state.filter(notification => notification.id !== action.payload);
    
    case NOTIFICATION_ACTIONS.CLEAR_ALL:
      return [];
    
    case NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION:
      return state.map(notification => 
        notification.id === action.payload.id 
          ? { ...notification, ...action.payload.updates }
          : notification
      );
    
    default:
      return state;
  }
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationReducer, []);

  // Add notification
  const addNotification = useCallback((notification) => {
    const id = Date.now().toString();
    const newNotification = {
      id,
      timestamp: new Date().toISOString(),
      duration: 5000, // Default 5 seconds
      ...notification
    };

    dispatch({
      type: NOTIFICATION_ACTIONS.ADD_NOTIFICATION,
      payload: newNotification
    });

    // Auto-remove after duration
    if (newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  }, []);

  // Remove notification
  const removeNotification = useCallback((id) => {
    dispatch({
      type: NOTIFICATION_ACTIONS.REMOVE_NOTIFICATION,
      payload: id
    });
  }, []);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    dispatch({
      type: NOTIFICATION_ACTIONS.CLEAR_ALL
    });
  }, []);

  // Update notification
  const updateNotification = useCallback((id, updates) => {
    dispatch({
      type: NOTIFICATION_ACTIONS.UPDATE_NOTIFICATION,
      payload: { id, updates }
    });
  }, []);

  // Convenience methods for different types
  const success = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.SUCCESS,
      message,
      ...options
    });
  }, [addNotification]);

  const error = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.ERROR,
      message,
      duration: 8000, // Longer duration for errors
      ...options
    });
  }, [addNotification]);

  const warning = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.WARNING,
      message,
      duration: 6000,
      ...options
    });
  }, [addNotification]);

  const info = useCallback((message, options = {}) => {
    return addNotification({
      type: NOTIFICATION_TYPES.INFO,
      message,
      ...options
    });
  }, [addNotification]);

  // Email notification simulation
  const sendEmail = useCallback(async (to, subject, body, options = {}) => {
    try {
      // Simulate email sending
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Email sent:', { to, subject, body });
      
      if (!options.silent) {
        success(`Email enviado exitosamente a ${to}`);
      }
      
      return { success: true, messageId: `email_${Date.now()}` };
    } catch (error) {
      console.error('Error sending email:', error);
      
      if (!options.silent) {
        error(`Error enviando email a ${to}: ${error.message}`);
      }
      
      return { success: false, error: error.message };
    }
  }, [success, error]);

  // SMS notification simulation
  const sendSMS = useCallback(async (to, message, options = {}) => {
    try {
      // Simulate SMS sending
      await new Promise(resolve => setTimeout(resolve, 800));
      
      console.log('SMS sent:', { to, message });
      
      if (!options.silent) {
        success(`SMS enviado exitosamente a ${to}`);
      }
      
      return { success: true, messageId: `sms_${Date.now()}` };
    } catch (error) {
      console.error('Error sending SMS:', error);
      
      if (!options.silent) {
        error(`Error enviando SMS a ${to}: ${error.message}`);
      }
      
      return { success: false, error: error.message };
    }
  }, [success, error]);

  // WhatsApp notification simulation
  const sendWhatsApp = useCallback(async (to, message, options = {}) => {
    try {
      // Simulate WhatsApp sending
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      console.log('WhatsApp sent:', { to, message });
      
      if (!options.silent) {
        success(`WhatsApp enviado exitosamente a ${to}`);
      }
      
      return { success: true, messageId: `whatsapp_${Date.now()}` };
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      
      if (!options.silent) {
        error(`Error enviando WhatsApp a ${to}: ${error.message}`);
      }
      
      return { success: false, error: error.message };
    }
  }, [success, error]);

  // Shipment status notification
  const notifyShipmentStatus = useCallback((shipment, status) => {
    const statusMessages = {
      processing: 'Su envío está siendo procesado',
      in_transit: 'Su envío está en camino',
      out_for_delivery: 'Su envío está siendo entregado',
      delivered: 'Su envío ha sido entregado exitosamente',
      cancelled: 'Su envío ha sido cancelado',
      exception: 'Ha ocurrido un problema con su envío'
    };

    const message = statusMessages[status] || `Estado de envío actualizado: ${status}`;
    
    const notificationData = {
      title: `Envío #${shipment.id}`,
      message: message,
      type: status === 'delivered' ? NOTIFICATION_TYPES.SUCCESS : 
            status === 'cancelled' || status === 'exception' ? NOTIFICATION_TYPES.ERROR :
            NOTIFICATION_TYPES.INFO,
      shipmentId: shipment.id,
      trackingNumber: shipment.trackingNumber
    };

    addNotification(notificationData);

    // Also send notifications via other channels if configured
    if (shipment.recipient && shipment.recipient.email) {
      sendEmail(
        shipment.recipient.email,
        `Silva Transport - ${notificationData.title}`,
        message,
        { silent: true }
      );
    }

    if (shipment.recipient && shipment.recipient.phone) {
      sendSMS(
        shipment.recipient.phone,
        `Silva Transport: ${message}. Tracking: ${shipment.trackingNumber}`,
        { silent: true }
      );
    }
  }, [addNotification, sendEmail, sendSMS]);

  // Balance low notification
  const notifyLowBalance = useCallback((currentBalance, threshold = 1000) => {
    if (currentBalance < threshold) {
      warning(
        `Saldo bajo: $${currentBalance.toFixed(2)}. Recarga tu cuenta para continuar realizando envíos.`,
        {
          title: 'Saldo Insuficiente',
          persistent: true,
          action: {
            label: 'Recargar',
            onClick: () => {
              // Navigate to payments page
              window.location.href = '/payments';
            }
          }
        }
      );
    }
  }, [warning]);

  // Payment notification
  const notifyPayment = useCallback((payment) => {
    const isSuccess = payment.status === 'completed';
    
    const notificationData = {
      title: isSuccess ? 'Pago Exitoso' : 'Error en el Pago',
      message: isSuccess 
        ? `Pago de $${payment.amount} procesado exitosamente`
        : `Error procesando pago de $${payment.amount}: ${payment.error}`,
      type: isSuccess ? NOTIFICATION_TYPES.SUCCESS : NOTIFICATION_TYPES.ERROR,
      paymentId: payment.id
    };

    addNotification(notificationData);
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    updateNotification,
    success,
    error,
    warning,
    info,
    sendEmail,
    sendSMS,
    sendWhatsApp,
    notifyShipmentStatus,
    notifyLowBalance,
    notifyPayment
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Alias for backward compatibility
export const useNotification = useNotifications;

export default NotificationContext;